import { getApiBase } from "./apiConfig";
import { logger } from "./logger";

const API_BASE = getApiBase();
const CAPITAL_MANAGEMENT_BASE = `${API_BASE}/api/capital-management`;

export interface SlotAllocationSnapshot {
  strategy: string;
  per_slot_capital: number;
  seats_total: number;
  seats_in_use: number;
  seats_available: number;
  slot_budget_remaining: number;
  available_capital: number;
  seat_share_for_request: number;
  last_updated_at: string;
}

export interface PortfolioState {
  total_equity: number;
  available_capital: number;
  allocated_capital: number;
  current_heat: number;
  max_heat: number;
  heat_percentage: number;
  open_positions_count: number;
  max_positions: number;
  available_risk_capacity: number;
  last_updated_at: string;
  slot_allocation?: SlotAllocationSnapshot | null;
}

export interface HeatStatus {
  current_heat: number;
  max_heat: number;
  heat_percentage: number;
  can_open_new_position: boolean;
  available_risk_capacity: number;
  risk_level: "low" | "medium" | "high" | "critical";
  warnings: string[];
}

export interface CorrelationAnalysis {
  symbol: string;
  max_correlation: number;
  correlated_positions: string[];
  adjustment_factor: number;
  recommendation: string;
  risk_level: "low" | "medium" | "high" | "critical";
}

export interface PositionSizeRequest {
  symbol: string;
  entry_price: number;
  stop_loss_price: number;
  risk_percent?: number;
  confidence?: number;
  account_balance?: number;
}

export interface AdjustmentDetail {
  type: string;
  factor: number;
  reason: string;
  original_value: number;
  adjusted_value: number;
}

export interface PositionSizeResult {
  position_size: number;
  risk_amount: number;
  risk_percentage: number;
  base_size: number;
  adjustments_applied: AdjustmentDetail[];
  final_multiplier: number;
  status: "approved" | "rejected" | "warning";
  can_execute: boolean;
  reason?: string;
  warnings: string[];
  calculated_at: string;
  slot_allocation?: SlotAllocationSnapshot | null;
}

export interface CapitalManagementConfig {
  risk_per_trade_percent: number;
  max_portfolio_heat_percent: number;
  max_position_risk_percent: number;
  correlation_threshold: number;
  correlation_critical_threshold: number;
  kelly_fraction: number;
  max_positions_small: number;
  max_positions_medium: number;
  max_positions_large: number;
  small_account_threshold: number;
  medium_account_threshold: number;
  atr_period: number;
  volatility_lookback_days: number;
  correlation_lookback_days: number;
  enable_kelly_criterion: boolean;
  enable_regime_detection: boolean;
  slippage_buffer_percent: number;
  slippage_alert_threshold_percent: number;
  enable_capital_reallocation: boolean;
  reallocation_pnl_threshold: number;
  reallocation_confidence_threshold: number;
  reallocation_hold_duration_hours: number;
  last_updated_at: string;
}

export interface QueuedTrade {
  recommendation_id: string;
  symbol: string;
  entry_price: number;
  stop_loss_price: number;
  confidence: number;
  calculated_position_size: number;
  queued_at: string;
  metadata: Record<string, unknown>;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Unknown error");
    throw new Error(`API error: ${response.status} - ${errorText}`);
  }

  try {
    return (await response.json()) as T;
  } catch (error) {
    throw new Error("Failed to parse response as JSON");
  }
}

export async function getPortfolioState(forceRefresh: boolean = true): Promise<PortfolioState> {
  const url = `${CAPITAL_MANAGEMENT_BASE}/portfolio/state${forceRefresh ? "?force_refresh=true" : ""}`;
  
  const response = await fetch(url, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  return handleResponse<PortfolioState>(response);
}

export async function getPortfolioHeat(): Promise<HeatStatus> {
  const response = await fetch(`${CAPITAL_MANAGEMENT_BASE}/portfolio/heat`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  return handleResponse<HeatStatus>(response);
}

export async function getCorrelationAnalysis(symbol: string): Promise<CorrelationAnalysis> {
  const response = await fetch(`${CAPITAL_MANAGEMENT_BASE}/correlation/${encodeURIComponent(symbol)}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  return handleResponse<CorrelationAnalysis>(response);
}

export async function calculatePositionSize(
  request: PositionSizeRequest
): Promise<PositionSizeResult> {
  const response = await fetch(`${CAPITAL_MANAGEMENT_BASE}/position-size/calculate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });

  return handleResponse<PositionSizeResult>(response);
}

export async function getCapitalManagementConfig(): Promise<CapitalManagementConfig> {
  const response = await fetch(`${CAPITAL_MANAGEMENT_BASE}/config`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  return handleResponse<CapitalManagementConfig>(response);
}

export async function updateCapitalManagementConfig(
  config: CapitalManagementConfig
): Promise<CapitalManagementConfig> {
  const response = await fetch(`${CAPITAL_MANAGEMENT_BASE}/config`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(config),
  });

  return handleResponse<CapitalManagementConfig>(response);
}

export async function getQueuedTrades(): Promise<QueuedTrade[]> {
  const response = await fetch(`${CAPITAL_MANAGEMENT_BASE}/queue`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  return handleResponse<QueuedTrade[]>(response);
}

export async function getQueueLength(): Promise<{ queue_length: number }> {
  const response = await fetch(`${CAPITAL_MANAGEMENT_BASE}/queue/length`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  return handleResponse<{ queue_length: number }>(response);
}

